import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { CAL_COLORS } from "../lib/demoData";
import { Button } from "./ui/button";

// In-app + browser reminders for dated missions due today or tomorrow. No external service.
export function MissionReminders() {
  const { data } = useApp();
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const calendar = data?.calendar || [];
  const actions = data?.actions || [];
  const doneIds = new Set(actions.filter((a) => a.done).map((a) => a.id));

  const now = new Date();
  const today = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const tomorrow = today >= daysInMonth ? 1 : today + 1;

  const due = calendar
    .filter((e) => e.missionId && !doneIds.has(e.missionId) && (e.day === today || e.day === tomorrow))
    .map((e) => ({ ...e, when: e.day === today ? "today" : "tomorrow" }))
    .sort((a, b) => (a.when === b.when ? 0 : a.when === "today" ? -1 : 1));

  const fire = useCallback(() => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    due.forEach((e) => {
      // Non-sensitive per-session dedup flag so a given reminder fires only once per day.
      const key = `lb_notif_${e.id}_${today}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
      const label = e.when === "today" ? "Aujourd'hui" : "Demain";
      try { new Notification(`Rappel : ${e.title}`, { body: `${label} · ${e.type} · ${e.channel || ""}` }); }
      catch (err) { console.error("Notification failed:", err); }
    });
  }, [due, today]);

  useEffect(() => { if (perm === "granted") fire(); }, [perm, fire]);

  const enable = async () => {
    if (typeof Notification === "undefined") { toast.error("Ce navigateur ne supporte pas les notifications."); return; }
    const p = await Notification.requestPermission();
    setPerm(p);
    if (p === "granted") { toast.success("Rappels activés"); fire(); }
    else toast.info("Notifications refusées — vous verrez quand même les rappels dans l'app.");
  };

  if (due.length === 0) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="lb-card p-4 border border-amber-500/25 bg-amber-500/[0.06]" data-testid="mission-reminders">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center text-amber-400 shrink-0"><BellRing size={18} /></div>
          <div>
            <p className="font-display font-bold text-base">Rappels de missions</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">{due.length} à préparer aujourd'hui ou demain</p>
          </div>
        </div>
        {perm !== "granted" && perm !== "unsupported" && (
          <Button data-testid="reminder-enable-notif" onClick={enable} variant="outline"
            className="rounded-full border-amber-500/30 bg-amber-500/10 text-amber-300 text-xs h-9">
            <Bell size={14} className="mr-1.5" /> Activer les notifications
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {due.map((e) => (
          <div key={e.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/5" data-testid={`reminder-item-${e.id}`}>
            <div className="w-2 h-8 rounded-full shrink-0" style={{ background: CAL_COLORS[e.type] || "#F59E0B" }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{e.title}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] capitalize">{e.type} · {e.channel}</p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${e.when === "today" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>
              {e.when === "today" ? "Aujourd'hui" : "Demain"}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
